/**
 * Branches & Factories — manage page.
 *
 * Reads and writes branches through the shared AppSettings context so that
 * changes persist across navigation and are visible to other pages that read
 * the same context.  An archive confirmation dialog warns the user when the
 * branch has linked warehouses or dealers before the mutation is applied.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Building2, Pencil, Plus, Trash2, Undo2 } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  BranchForm,
  type BranchFormValues,
  type BranchType,
} from "../../../components/forms/BranchForm";
import { mockWarehouses, type MockWarehouse } from "../../../lib/inventoryMock";
import { mockDealers, type MockDealer } from "../../../lib/salesMock";
import { useAppSettings, type BranchRow } from "../../../lib/appSettings";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/settings/branches")({
  component: BranchesPage,
});

/* ─────────────────────────────────────────────────────────────
 * Local types
 * ───────────────────────────────────────────────────────────── */

const BRANCH_TYPE_LABELS: Record<BranchType, string> = {
  hq: "HQ",
  factory: "Factory",
  sales: "Sales",
};

const BRANCH_TYPE_VARIANT: Record<
  BranchType,
  "default" | "warning" | "info" | "neutral"
> = {
  hq: "default",
  factory: "warning",
  sales: "info",
};

type StatusFilter = "all" | "active" | "archived";
type TypeFilter = "all" | BranchType;

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function BranchesPage() {
  const { branches, updateBranches } = useAppSettings();
  const [warehouses] = usePersistentMockState<MockWarehouse[]>(
    "warehouses",
    mockWarehouses,
  );
  const [dealers] = usePersistentMockState<MockDealer[]>(
    "dealers",
    mockDealers,
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<BranchRow | null>(null);
  const [detailRow, setDetailRow] = useState<BranchRow | null>(null);

  // Archive confirmation dialog target
  const [archiveTarget, setArchiveTarget] = useState<BranchRow | null>(null);

  // Derived: warehouses count + linked-dealer count keyed by branch id.
  const warehousesByBranch = useMemo(() => {
    const map = new Map<string, number>();
    for (const w of warehouses) {
      map.set(w.branchId, (map.get(w.branchId) ?? 0) + 1);
    }
    return map;
  }, [warehouses]);
  const dealersByBranch = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of dealers) {
      map.set(d.branchId, (map.get(d.branchId) ?? 0) + 1);
    }
    return map;
  }, [dealers]);

  const filtered = useMemo(() => {
    return branches.filter((b) => {
      if (typeFilter !== "all" && b.branchType !== typeFilter) return false;
      if (statusFilter === "active" && b.deletedAt !== null) return false;
      if (statusFilter === "archived" && b.deletedAt === null) return false;
      return true;
    });
  }, [branches, typeFilter, statusFilter]);

  const kpis = useMemo(() => {
    const live = branches.filter((b) => b.deletedAt === null);
    return {
      total: live.length,
      active: live.filter((b) => b.isActive).length,
      factories: live.filter((b) => b.branchType === "factory").length,
      sales: live.filter((b) => b.branchType === "sales").length,
    };
  }, [branches]);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: BranchRow) {
    setEditing(row);
    setDetailRow(null);
    setSheetOpen(true);
  }

  function handleSubmit(values: BranchFormValues) {
    if (editing) {
      updateBranches((prev) =>
        prev.map((b) =>
          b._id === editing._id
            ? {
                ...b,
                displayName: values.displayName,
                branchType: values.branchType,
                addressLine: values.address.line1,
                city: values.address.city,
                contactPhone: values.contactPhone || null,
                isActive: values.isActive,
              }
            : b,
        ),
      );
      toast.success("Branch updated. Audit trail recorded.");
    } else {
      const newId = `branch_${Date.now()}`;
      updateBranches((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          branchType: values.branchType,
          city: values.address.city,
          addressLine: values.address.line1,
          contactPhone: values.contactPhone || null,
          isActive: values.isActive,
          deletedAt: null,
        },
      ]);
      toast.success("Branch created (mock)");
    }
    setSheetOpen(false);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    const target = archiveTarget;
    updateBranches((prev) =>
      prev.map((b) =>
        b._id === target._id
          ? { ...b, isActive: false, deletedAt: Date.now() }
          : b,
      ),
    );
    setDetailRow(null);
    setArchiveTarget(null);
    toast.success("Branch archived");
  }

  function handleRestore(row: BranchRow) {
    updateBranches((prev) =>
      prev.map((b) =>
        b._id === row._id ? { ...b, isActive: true, deletedAt: null } : b,
      ),
    );
    setDetailRow(null);
    toast.success("Branch restored");
  }

  const columns = useMemo<DataTableColumn<BranchRow>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        meta: { mono: true },
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "branchType",
        header: "Type",
        cell: (info) => {
          const t = info.row.original.branchType;
          return (
            <Badge variant={BRANCH_TYPE_VARIANT[t]} size="sm">
              {BRANCH_TYPE_LABELS[t]}
            </Badge>
          );
        },
      },
      {
        id: "address",
        header: "Address",
        cell: (info) => (
          <span className="text-(--text-secondary)">
            {info.row.original.addressLine}, {info.row.original.city}
          </span>
        ),
      },
      {
        id: "warehouses",
        header: "Warehouses",
        cell: (info) => (
          <Badge variant="neutral" size="sm">
            {warehousesByBranch.get(info.row.original._id) ?? 0}
          </Badge>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => {
          const r = info.row.original;
          if (r.deletedAt !== null) {
            return (
              <Badge variant="neutral" size="sm">
                Archived
              </Badge>
            );
          }
          return (
            <Badge variant={r.isActive ? "success" : "neutral"} size="sm">
              {r.isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
    ],
    [warehousesByBranch],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Branches & Factories"
        description="Physical and legal sites — head office, factories, sales offices. Codes are referenced everywhere stock and sales orders are recorded."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add branch
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard caption="Total branches" value={kpis.total.toString()} />
        <KpiCard caption="Active" value={kpis.active.toString()} />
        <KpiCard caption="Factories" value={kpis.factories.toString()} />
        <KpiCard caption="Sales offices" value={kpis.sales.toString()} />
      </div>

      <FilterBar
        typeFilter={typeFilter}
        onTypeChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
      />

      <DataTable
        data={filtered}
        columns={columns}
        emptyTitle="No branches match"
        emptyDescription="Adjust filters or create a new branch."
        emptyIcon={<Building2 className="size-5 text-(--text-tertiary)" />}
        onRowClick={(row) => setDetailRow(row.original)}
      />

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent size="md">
          {detailRow ? (
            <BranchDetail
              branch={detailRow}
              warehouses={warehouses}
              warehousesCount={warehousesByBranch.get(detailRow._id) ?? 0}
              dealersCount={dealersByBranch.get(detailRow._id) ?? 0}
              onEdit={() => openEdit(detailRow)}
              onArchive={() => setArchiveTarget(detailRow)}
              onRestore={() => handleRestore(detailRow)}
              onClose={() => setDetailRow(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit branch" : "New branch"}</SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the branch details. Code is locked once created."
                : "Codes appear on every stock movement and sales order — keep them short and stable."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <BranchForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      branchType: editing.branchType,
                      address: {
                        line1: editing.addressLine,
                        line2: "",
                        city: editing.city,
                        country: "Bangladesh",
                      },
                      contactPhone: editing.contactPhone ?? "",
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Archive confirmation dialog */}
      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-(--warning)" />
              Archive {archiveTarget?.displayName}?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-2 text-[13px]">
                <p>This branch will be marked archived and hidden from new records.</p>
                {archiveTarget && warehousesByBranch.get(archiveTarget._id) ? (
                  <div className="rounded-md border border-(--warning-soft) bg-(--warning-soft)/40 px-3 py-2 text-(--warning)">
                    &#9888; {warehousesByBranch.get(archiveTarget._id)} warehouse(s) are linked to this branch. They will remain but this branch won't be available for new assignments.
                  </div>
                ) : null}
                {archiveTarget && dealersByBranch.get(archiveTarget._id) ? (
                  <div className="rounded-md border border-(--warning-soft) bg-(--warning-soft)/40 px-3 py-2 text-(--warning)">
                    &#9888; {dealersByBranch.get(archiveTarget._id)} dealer(s) are linked to this branch. Their records are preserved.
                  </div>
                ) : null}
                <p className="text-(--text-tertiary) text-[12px]">Existing records that reference this branch will still work — they keep their historical data.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmArchive}>Archive branch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Filter chips
 * ───────────────────────────────────────────────────────────── */

function FilterBar({
  typeFilter,
  onTypeChange,
  statusFilter,
  onStatusChange,
}: {
  typeFilter: TypeFilter;
  onTypeChange: (v: TypeFilter) => void;
  statusFilter: StatusFilter;
  onStatusChange: (v: StatusFilter) => void;
}) {
  const typeOptions: { value: TypeFilter; label: string }[] = [
    { value: "all", label: "All types" },
    { value: "hq", label: "HQ" },
    { value: "factory", label: "Factory" },
    { value: "sales", label: "Sales" },
  ];
  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mr-1">
          Type
        </span>
        {typeOptions.map((opt) => (
          <Chip
            key={opt.value}
            active={typeFilter === opt.value}
            onClick={() => onTypeChange(opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mr-1">
          Status
        </span>
        {statusOptions.map((opt) => (
          <Chip
            key={opt.value}
            active={statusFilter === opt.value}
            onClick={() => onStatusChange(opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 h-7 text-[12px] transition-colors",
        active
          ? "bg-(--bg-selected) border-(--accent) text-(--accent)"
          : "bg-(--bg-canvas) border-(--border-subtle) text-(--text-secondary) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet body
 * ───────────────────────────────────────────────────────────── */

function BranchDetail({
  branch,
  warehouses,
  warehousesCount,
  dealersCount,
  onEdit,
  onArchive,
  onRestore,
  onClose,
}: {
  branch: BranchRow;
  warehouses: MockWarehouse[];
  warehousesCount: number;
  dealersCount: number;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onClose: () => void;
}) {
  const branchWarehouses = useMemo(
    () => warehouses.filter((w) => w.branchId === branch._id),
    [warehouses, branch._id],
  );
  const isArchived = branch.deletedAt !== null;
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{branch.code}</span>
          <Badge variant={BRANCH_TYPE_VARIANT[branch.branchType]} size="sm">
            {BRANCH_TYPE_LABELS[branch.branchType]}
          </Badge>
          {isArchived ? (
            <Badge variant="neutral" size="sm">
              Archived
            </Badge>
          ) : (
            <Badge
              variant={branch.isActive ? "success" : "neutral"}
              size="sm"
            >
              {branch.isActive ? "Active" : "Inactive"}
            </Badge>
          )}
          <RecordAuditBadge tableName="branches" recordId={branch._id} />
        </SheetTitle>
        <SheetDescription>{branch.displayName}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="warehouses">
              Warehouses ({warehousesCount})
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              <section className="grid grid-cols-2 gap-3">
                <DetailField label="Code" value={branch.code} />
                <DetailField
                  label="Type"
                  value={BRANCH_TYPE_LABELS[branch.branchType]}
                />
                <DetailField label="City" value={branch.city} />
                <DetailField
                  label="Phone"
                  value={branch.contactPhone ?? "—"}
                />
                <DetailField
                  label="Warehouses"
                  value={warehousesCount.toString()}
                />
                <DetailField
                  label="Linked dealers"
                  value={dealersCount.toString()}
                />
              </section>
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Address
                </div>
                <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3 text-[13px] text-(--text-primary)">
                  {branch.addressLine}
                  <br />
                  {branch.city}, Bangladesh
                </div>
              </section>
            </div>
          </TabsContent>
          <TabsContent value="warehouses" className="mt-4">
            {branchWarehouses.length === 0 ? (
              <div className="rounded-md border border-dashed border-(--border-subtle) p-4 text-[12px] text-(--text-tertiary) text-center">
                No warehouses linked to this branch yet.
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {branchWarehouses.map((w) => (
                  <li
                    key={w._id}
                    className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2"
                  >
                    <div>
                      <div className="font-mono text-[12px] text-(--text-primary)">
                        {w.code}
                      </div>
                      <div className="text-[12px] text-(--text-secondary)">
                        {w.displayName}
                      </div>
                    </div>
                    <Badge variant="neutral" size="sm">
                      {w.warehouseType.replace(/_/g, " ")}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="branches" recordId={branch._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {isArchived ? (
          <Button variant="secondary" onClick={onRestore}>
            <Undo2 className="size-3.5" /> Restore
          </Button>
        ) : (
          <Button variant="secondary" onClick={onArchive}>
            <Trash2 className="size-3.5" /> Archive
          </Button>
        )}
        <Button size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}
